import { useMutation, useQueryClient, queryOptions } from '@tanstack/react-query'
import { apiFetch } from './http'
import { qk } from './queryKeys'
import type {
  ApplyResellerRequest,
  ConnectOnboardingLinkResponse,
  CreateReferredCompanyRequest,
  CreateReferredCompanyResponse,
  InviteResellerRequest,
  InviteResellerResponse,
  RejectResellerRequest,
  PublicReseller,
  Reseller,
  ReferralsResponse,
  ResellerStatus,
  UpdateDirectoryListingRequest,
} from './types'

export const resellersApi = {
  apply: (b: ApplyResellerRequest) => apiFetch<{ id: string }>('/resellers/apply', { method: 'POST', body: b, auth: false }),
  list: (status: ResellerStatus | undefined, signal?: AbortSignal) =>
    apiFetch<Reseller[]>(status ? `/resellers?status=${status}` : '/resellers', { signal }),
  approve: (id: string) => apiFetch<Reseller>(`/resellers/${id}/approve`, { method: 'POST' }),
  invite: (b: InviteResellerRequest) => apiFetch<InviteResellerResponse>('/resellers/invite', { method: 'POST', body: b }),
  resendInvite: (id: string) => apiFetch<void>(`/resellers/${id}/resend-invite`, { method: 'POST' }),
  reject: (id: string, b: RejectResellerRequest) => apiFetch<Reseller>(`/resellers/${id}/reject`, { method: 'POST', body: b }),
  directory: (signal?: AbortSignal) => apiFetch<PublicReseller[]>('/resellers/directory', { signal, auth: false }),
  updateListing: (b: UpdateDirectoryListingRequest) => apiFetch<Reseller>('/resellers/me/listing', { method: 'PUT', body: b }),
  me: (signal?: AbortSignal) => apiFetch<Reseller>('/resellers/me', { signal }),
  myReferrals: (signal?: AbortSignal) => apiFetch<ReferralsResponse>('/resellers/me/referrals', { signal }),
  connectOnboardingLink: () => apiFetch<ConnectOnboardingLinkResponse>('/resellers/me/connect-onboarding-link', { method: 'POST' }),
  createReferredCompany: (b: CreateReferredCompanyRequest) => apiFetch<CreateReferredCompanyResponse>('/resellers/me/companies', { method: 'POST', body: b }),
  resendWelcome: (companyId: string) => apiFetch<void>(`/resellers/me/companies/${companyId}/resend-welcome`, { method: 'POST' }),
}

export const resellerQueries = {
  list: (status?: ResellerStatus) =>
    queryOptions({ queryKey: qk.resellers.list(status), queryFn: ({ signal }) => resellersApi.list(status, signal), staleTime: 30_000 }),
  directory: () => queryOptions({ queryKey: qk.resellers.directory, queryFn: ({ signal }) => resellersApi.directory(signal), staleTime: 5 * 60_000 }),
  me: () => queryOptions({ queryKey: qk.resellers.me, queryFn: ({ signal }) => resellersApi.me(signal), staleTime: 30_000 }),
  myReferrals: () => queryOptions({ queryKey: qk.resellers.myReferrals, queryFn: ({ signal }) => resellersApi.myReferrals(signal), staleTime: 30_000 }),
}

export function useConnectOnboardingLink() {
  return useMutation({ mutationFn: resellersApi.connectOnboardingLink })
}

export function useCreateReferredCompany() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: resellersApi.createReferredCompany, onSuccess: () => qc.invalidateQueries({ queryKey: qk.resellers.myReferrals }) })
}
export function useResendWelcome() {
  return useMutation({ mutationFn: resellersApi.resendWelcome })
}
export function useApplyReseller() {
  return useMutation({ mutationFn: resellersApi.apply })
}
export function useApproveReseller() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: resellersApi.approve, onSuccess: () => qc.invalidateQueries({ queryKey: qk.resellers.all }) })
}
export function useInviteReseller() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: resellersApi.invite, onSuccess: () => qc.invalidateQueries({ queryKey: qk.resellers.all }) })
}
export function useResendResellerInvite() {
  return useMutation({ mutationFn: resellersApi.resendInvite })
}
export function useRejectReseller() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...b }: RejectResellerRequest & { id: string }) => resellersApi.reject(id, b),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.resellers.all }),
  })
}
export function useUpdateDirectoryListing() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: resellersApi.updateListing, onSuccess: () => qc.invalidateQueries({ queryKey: qk.resellers.all }) })
}
